import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../_services/authentication.service';
import { AccountService } from '../../_services/account.service';
import { FilterRegistryService } from '../../_services/filter-registry.service';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  error?: boolean;
  actionResult?: {
    action: string;
    message: string;
    success: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AiAssistantService {
  // URL del Gateway de SIRA AI
  private apiUrl = 'https://sira-agent.twa.es/v1';

  public isOpen = false;
  public isGenerating = false;
  public isContinuityRunning = false;
  public continuityTargetLabel: string | null = null;
  public messages: ChatMessage[] = [];
  public sessionId: string;

  private currentAbortController: AbortController | null = null;
  private continuityCancelled = false;
  private lastUserQuery = '';

  constructor(
    private authService: AuthenticationService,
    private accountService: AccountService,
    private router: Router,
    public filterRegistry: FilterRegistryService
  ) {
    this.sessionId = this.getOrCreateSessionId();
    this.initWelcomeMessage();
  }

  private getOrCreateSessionId(): string {
    let sid = localStorage.getItem('sira_ai_session_id');
    if (!sid) {
      sid = 'sira-' + Math.random().toString(36).substring(2, 11) + '-' + Date.now();
      localStorage.setItem('sira_ai_session_id', sid);
    }
    return sid;
  }

  private initWelcomeMessage() {
    this.messages = [
      {
        id: 'msg-welcome',
        role: 'assistant',
        content: '¡Hola! Soy el asistente técnico de **SIRA**. Puedo ayudarte con dudas sobre el funcionamiento de la plataforma, normas de validación de partos y montas, o cómo interpretar y subsanar incidencias en tus partes.\n\n¿En qué puedo ayudarte hoy?',
        timestamp: new Date()
      }
    ];
  }

  public toggleOpen() {
    this.isOpen = !this.isOpen;
  }

  public open() {
    this.isOpen = true;
  }

  public close() {
    this.isOpen = false;
  }

  public async sendMessage(userText: string, context?: { selectedCEA?: string }) {
    if (!userText || !userText.trim() || this.isGenerating) {
      return;
    }

    const trimmed = userText.trim();
    this.lastUserQuery = trimmed;

    // Mensaje del usuario
    const userMsg: ChatMessage = {
      id: 'msg-' + Date.now(),
      role: 'user',
      content: trimmed,
      timestamp: new Date()
    };
    this.messages.push(userMsg);

    // Mensaje del asistente vacío para streaming
    const assistantMsg: ChatMessage = {
      id: 'msg-' + (Date.now() + 1),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    };
    this.messages.push(assistantMsg);

    await this.generateAssistantResponse(trimmed, assistantMsg, false, context?.selectedCEA);
  }

  private async generateAssistantResponse(
    promptText: string,
    assistantMsg: ChatMessage,
    isContinuity: boolean = false,
    selectedCEA?: string
  ): Promise<void> {
    const currentRoute = this.router.url;
    const cea = selectedCEA || localStorage.getItem('selectedCEA') || this.authService.user?.Explotacion || '';
    const userRole = this.authService.user?.Usuario || this.authService.user?.Email || 'ganadero';
    const serverInfo = this.accountService?.serverInfo;
    const clientName = serverInfo?.customer || this.authService.user?.Cliente || this.accountService?.data?.nombre || '';
    const serverType = serverInfo?.server_type || '';
    const serverDomain = serverInfo?.domain || window.location.hostname;

    const contextHeaders: string[] = [];
    contextHeaders.push(
      `[Contexto SIRA: Cliente/Asociación="${clientName || 'SIRA'}", SIRA_SERVER_TYPE=${serverType || '1'}, Dominio="${serverDomain}", CEA=${cea || 'N/A'}, Ruta="${currentRoute}", Usuario="${userRole}"]`
    );

    if (!isContinuity) {
      contextHeaders.push(
        `[INSTRUCCIÓN DE NAVEGACIÓN: Si el usuario te pide ir o navegar a una sección de la aplicación (ej. Libros, Trabajos Pendientes, Censo Animal, Partes, Crías, Lactaciones, Inseminaciones, Ganaderías, Informes, etc.), o si su pregunta requiere datos que se encuentran en otra sección, incluye al final de tu respuesta el bloque:\n` +
        `\`\`\`sira-action\n` +
        `{\n` +
        `  "action": "navigate",\n` +
        `  "route": "/libros" | "/pending" | "/animals" | "/partes" | "/crias" | "/lactaciones" | "/inseminaciones" | "/ganaderias" | "/informes" | "/imports" | "/scales" | "/dashboard",\n` +
        `  "label": "Nombre amigable de la sección",\n` +
        `  "pending_query": "Consulta o pregunta que el usuario quiere responder en esa sección"\n` +
        `}\n` +
        `\`\`\`\n` +
        `La aplicación navegará automáticamente a esa pantalla y continuará con la respuesta usando los datos cargados.]`
      );
    }

    const filterCtx = this.filterRegistry.getContext();
    if (filterCtx.hasActiveEditor) {
      const fieldList = filterCtx.fields.map(f => `${f.name} ("${f.label}")`).join(', ');
      const activeFiltersStr = filterCtx.activeFilters.length > 0
        ? filterCtx.activeFilters.map(f => f.explanation || `${f.name} ${f.operation} ${f.value}`).join(' AND ')
        : 'ninguno';

      contextHeaders.push(
        `[Tabla activa en pantalla: "${filterCtx.title || currentRoute}"]\n` +
        `[Campos de filtrado disponibles: ${fieldList}]\n` +
        `[Filtros actualmente aplicados: ${activeFiltersStr}]\n` +
        `[INSTRUCCIÓN DE FILTRADO: Si el usuario te pide filtrar, limpiar o cambiar los filtros de la tabla actual, incluye al final de tu respuesta el bloque:\n` +
        `\`\`\`sira-action\n` +
        `{\n` +
        `  "action": "set_filters" | "add_filter" | "clear_filters",\n` +
        `  "filters": [{ "field": "<campo>", "operation": "contain"|"equal"|">="|"<="|"!="|"is empty"|"is not empty", "value": "<valor>", "bitwise": "&&"|"||" }]\n` +
        `}\n` +
        `\`\`\`\n` +
        `Soporta agrupaciones recursivas: { "isgroup": true, "bitwise": "||", "fields": [...] } para filtros disyuntivos (ej. censo a fecha: ([alta <= F] && [baja empty]) || ([alta <= F] && [baja > F]), o aislar múltiples errores en pendientes). El sistema ejecutará automáticamente la acción en la tabla.]`
      );

      if (filterCtx.tableData) {
        const td = filterCtx.tableData;
        const pagedInfo = (td as any).isServerPaged
          ? ` (Paginación en servidor: mostrando ${td.sampleRows?.length || 0} filas en la página actual de ${td.totalRows} en total en BD)`
          : '';
        const dataLines: string[] = [
          `[Datos en la tabla actual: Total registros=${td.totalRows}, Visibles/filtrados=${td.filteredRows}${pagedInfo}]`
        ];

        if (td.columnDistributions && Object.keys(td.columnDistributions).length > 0) {
          const distParts: string[] = [];
          for (const [col, counts] of Object.entries(td.columnDistributions)) {
            const countsStr = Object.entries(counts).map(([v, n]) => `${v}: ${n}`).join(', ');
            distParts.push(`- ${col}: [${countsStr}]`);
          }
          const distScope = (td as any).isServerPaged
            ? 'calculado en servidor para el total de registros en BD'
            : 'en la tabla cargada';
          dataLines.push(`[Agrupación y recuento automático de registros (${distScope}):\n${distParts.join('\n')}]`);
        }

        if ((td as any).pendingErrorsSummary && Object.keys((td as any).pendingErrorsSummary).length > 0) {
          const errLines: string[] = [];
          for (const [code, info] of Object.entries((td as any).pendingErrorsSummary)) {
            const inf = info as any;
            const desc = inf.descripcion ? ` - "${inf.descripcion}"` : '';
            const trabs = inf.trabajos && inf.trabajos.length ? ` en [${inf.trabajos.join(', ')}]` : '';
            errLines.push(`  * Código ${code}${desc}${trabs}: ${inf.count} registro(s)`);
          }
          dataLines.push(`[Desglose global de errores detectados en Trabajos Pendientes:\n${errLines.join('\n')}\nIMPORTANTE: Puedes filtrar directamente por estos errores usando {"field": "errores", "operation": "contain", "value": "<codigo>"}]`);
        }

        if (td.sampleRows && td.sampleRows.length > 0) {
          dataLines.push(`[Muestra de registros visibles en pantalla (hasta ${td.sampleRows.length} filas):\n${JSON.stringify(td.sampleRows)}]`);
        }

        if (td.selectedItem) {
          dataLines.push(`[Elemento/Trabajo actualmente seleccionado por el usuario en pantalla:\n${JSON.stringify(td.selectedItem, null, 2)}]`);
        }

        contextHeaders.push(dataLines.join('\n'));
      }
    }

    const promptWithContext = `${contextHeaders.join('\n')}\n\n${promptText}`;

    this.isGenerating = true;
    this.currentAbortController = new AbortController();

    try {
      const response = await fetch(`${this.apiUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': this.sessionId
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: promptWithContext }],
          stream: true,
          user: userRole
        }),
        signal: this.currentAbortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('No readable stream received from AI Gateway');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || trimmedLine.startsWith(':')) continue;

          if (trimmedLine.startsWith('data: ')) {
            const dataStr = trimmedLine.substring(6).trim();
            if (dataStr === '[DONE]') {
              break;
            }

            try {
              const parsed = JSON.parse(dataStr);
              const deltaContent = parsed.choices?.[0]?.delta?.content;
              if (deltaContent) {
                assistantMsg.content += deltaContent;
              }
            } catch (_) {}
          }
        }
      }

      await this.processAssistantResponseActions(assistantMsg, isContinuity);
      assistantMsg.isStreaming = false;

    } catch (err: any) {
      if (err.name === 'AbortError') {
        assistantMsg.content += '\n*(Generación detenida)*';
      } else {
        console.error('[SIRA AI] Error al conectar con el Gateway:', err);
        assistantMsg.content = '⚠️ Lo siento, no pude conectar con el servicio de IA (sira-agent.twa.es). Asegúrate de que el Gateway de SIRA está activo.';
        assistantMsg.error = true;
      }
      assistantMsg.isStreaming = false;
    } finally {
      this.isGenerating = false;
      this.currentAbortController = null;
    }
  }

  private async processAssistantResponseActions(assistantMsg: ChatMessage, isContinuity: boolean = false): Promise<void> {
    if (!assistantMsg.content) return;

    // Detectar bloques ```sira-action o ```json o <sira-action>
    const codeMatch = assistantMsg.content.match(/```(?:sira-action|json)?\s*(\{\s*"action"[\s\S]*?\})\s*```/i);
    const tagMatch = assistantMsg.content.match(/<sira-action>\s*(\{[\s\S]*?\})\s*<\/sira-action>/i);

    const jsonStr = codeMatch ? codeMatch[1] : (tagMatch ? tagMatch[1] : null);
    if (!jsonStr) return;

    try {
      const payload = JSON.parse(jsonStr);
      if (payload && payload.action) {
        if (payload.action === 'navigate' && payload.route) {
          if (!isContinuity) {
            const targetRoute = payload.route;
            const targetLabel = payload.label || targetRoute;

            let pendingQuery = (payload.pending_query || payload.query || '').trim();
            if (!pendingQuery && this.lastUserQuery && this.looksLikeQuery(this.lastUserQuery)) {
              pendingQuery = this.lastUserQuery;
            }

            this.router.navigateByUrl(targetRoute);
            assistantMsg.actionResult = {
              action: 'navigate',
              message: payload.label ? `Navegando a ${payload.label}` : `Navegando a ${targetRoute}`,
              success: true
            };

            if (pendingQuery && !this.isContinuityRunning) {
              this.executeNavigationContinuity(targetRoute, targetLabel, pendingQuery);
            }
          }
        } else {
          const result = await this.filterRegistry.executeAction(payload);
          assistantMsg.actionResult = {
            action: payload.action,
            message: result.message,
            success: result.success
          };
        }
        // Limpiamos el bloque JSON del mensaje visible
        assistantMsg.content = assistantMsg.content
          .replace(/```(?:sira-action|json)?\s*\{\s*"action"[\s\S]*?\}\s*```/gi, '')
          .replace(/<sira-action>\s*\{[\s\S]*?\}\s*<\/sira-action>/gi, '')
          .trim();
      }
    } catch (e) {
      console.warn('[SIRA AI] Error al procesar acción:', e);
    }
  }

  private looksLikeQuery(text: string): boolean {
    if (!text) return false;
    const t = text.trim().toLowerCase();
    if (/^(?:ir|ve|vete|ir a|ve a|ll[eé]vame a|abrir?|navega(?:r)? a)\s+[a-záéíóú\s]+$/i.test(t)) {
      return false;
    }
    return t.includes('?') ||
      /\b(dime|muestra|cu[aá]nt|recuent|agrup|resum|total|qu[eé]|c[oó]mo|ver|busca|filtra|lista)\b/i.test(t);
  }

  private waitForRoute(targetRoute: string, timeoutMs: number = 3000): Promise<void> {
    return new Promise((resolve) => {
      if (this.router.url.includes(targetRoute)) {
        return resolve();
      }
      const startTime = Date.now();
      const interval = setInterval(() => {
        if (this.router.url.includes(targetRoute) || (Date.now() - startTime) >= timeoutMs) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    });
  }

  private async executeNavigationContinuity(targetRoute: string, targetLabel: string, pendingQuery: string): Promise<void> {
    if (this.isContinuityRunning) return;
    this.isContinuityRunning = true;
    this.continuityCancelled = false;
    this.continuityTargetLabel = targetLabel;

    const continuationMsg: ChatMessage = {
      id: 'msg-' + Date.now(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    };
    this.messages.push(continuationMsg);

    try {
      // 1. Esperar a que la navegación de la ruta se complete
      await this.waitForRoute(targetRoute, 3000);
      if (this.continuityCancelled) return;

      // 2. Esperar a que los datos de la tabla de la nueva ruta estén listos
      await this.filterRegistry.waitForTableDataReady(4500);
      if (this.continuityCancelled) return;

      // Breve pausa para estabilización de renderizado y bindings
      await new Promise(r => setTimeout(r, 150));
      if (this.continuityCancelled) return;

      this.continuityTargetLabel = null;

      // 3. Prompt de continuidad enriquecido
      const continuationPrompt = `[CONTINUACIÓN TRAS NAVEGACIÓN A ${targetLabel.toUpperCase()}: El usuario solicitó previamente: "${pendingQuery}". Ya hemos navegado a la pantalla de ${targetLabel} (${targetRoute}) y los datos de la tabla están cargados. Responde DIRECTAMENTE a su consulta utilizando los datos y recuentos de la tabla actual. No vuelvas a emitir acción de navegación.]`;

      await this.generateAssistantResponse(continuationPrompt, continuationMsg, true);

    } catch (err) {
      console.warn('[SIRA AI] Error en continuidad de navegación:', err);
      if (!continuationMsg.content) {
        continuationMsg.content = `He llegado a ${targetLabel}. Ya puedes consultar los datos en esta pantalla.`;
      }
      continuationMsg.isStreaming = false;
    } finally {
      this.isContinuityRunning = false;
      this.continuityTargetLabel = null;
    }
  }

  public stopGeneration() {
    this.continuityCancelled = true;
    this.isContinuityRunning = false;
    this.continuityTargetLabel = null;
    if (this.currentAbortController) {
      this.currentAbortController.abort();
    }
  }

  public async resetSession() {
    this.stopGeneration();

    try {
      await fetch(`${this.apiUrl}/sessions/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': this.sessionId
        },
        body: JSON.stringify({ session_id: this.sessionId })
      });
    } catch (_) {}

    localStorage.removeItem('sira_ai_session_id');
    this.sessionId = this.getOrCreateSessionId();
    this.initWelcomeMessage();
  }
}
